import { useEffect, useState } from 'react';
import { Table, Button, Container, Form, Modal, Row, Col } from 'react-bootstrap';
import { Pagination } from 'react-bootstrap';
import { fetchContacts, fetchContactDetails, deleteContact } from '../services/api';
import { updateContact } from '../services/api';
import { exportToExcel } from '../services/exportToExcel';
import { Contact } from '../types/Contact';

// --- COMPONENTE AUXILIAR MOVILIZADO FUERA DE CONTACTSPAGE ---

// Estilos internos para asegurar la compacidad del modal y que el label se vea más pequeño
const compactStyles = `
.small-label {
  font-size: 0.8rem;
  padding-top: 0.35rem; /* Ajuste para centrar mejor con el input compacto */
  padding-bottom: 0.35rem;
}
.compact-input {
  font-size: 0.85rem !important; 
  height: calc(1.5em + 0.5rem + 2px); 
  padding: 0.25rem 0.5rem; 
}
.small-modal-body {
    padding-top: 5px;
    padding-bottom: 5px;
}
.form-group-compact {
    margin-bottom: 0.5rem !important; /* Reducido a mb-1 */
}
`;

// Componente auxiliar para crear un campo en una sola línea y compacto
const RenderInlineField = ({ label, field, type = 'text', error, contact, setContact, required = false, readOnly = false }: 
  { label: string, field: keyof Contact, type?: string, error?: string, contact: Contact, setContact: React.Dispatch<React.SetStateAction<Contact | null>>, required?: boolean, readOnly?: boolean }
) => (
  <Form.Group as={Row} className="form-group-compact d-flex align-items-center">
    
    {/* Etiqueta (3 columnas, alineación a la derecha) */}
    <Form.Label column sm="3" className="small-label text-end">
      {label}
      {required && <span className="text-danger">*</span>}
    </Form.Label>
    
    {/* Campo de Entrada (9 columnas) */}
    <Col sm="9">
      <Form.Control
        type={type}
        value={contact[field] as string || ''}
        onChange={(e) =>
          setContact({ ...contact, [field]: e.target.value as string }) // Usando string más seguro
        }
        isInvalid={!!error}
        className="compact-input" // Clase para reducir tamaño
        readOnly={readOnly}
        plaintext={readOnly} // Para campos de solo lectura, usa plaintext para que se vea como texto
      />
      {error && (
        <Form.Control.Feedback type="invalid">
          {error}
        </Form.Control.Feedback>
      )}
    </Col>
  </Form.Group>
);

// --- FIN COMPONENTE AUXILIAR ---

function ContactsPage() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [search, setSearch] = useState('');
  const [empresaFilter, setEmpresaFilter] = useState('');
  const [cargoFilter, setCargoFilter] = useState('');  
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showModal, setShowModal] = useState(false);
  const token = localStorage.getItem('authToken') || '';
  const [loading, setLoading] = useState(false);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const loadContacts = async () => {
    setLoading(true); // ⏳ empieza a cargar
  
    try {

      const data = await fetchContacts(search, token, page, pageSize, empresaFilter, cargoFilter);
      
      setContacts(data.data);
      setTotal(data.meta.total_items ?? 0);

    } catch (err) {
      console.error('Error al cargar contactos:', err);
    } finally {
      setLoading(false); // ✅ termina la carga
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const handleDetails = async (id: number) => {
    try {
      const contact = await fetchContactDetails(id, token);
      setSelectedContact(contact);
      setShowModal(true);
    } catch (err) {
      console.error('Error al obtener detalles:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar este contacto?')) return;
    try {
      await deleteContact(id, token);
      loadContacts();
    } catch (err) {
      console.error('Error al eliminar contacto:', err);
    }
  };

  useEffect(() => {
    if (search.trim() === '' && empresaFilter.trim() === '' && cargoFilter.trim() === '') return; // ⛔ evita ejecutar si no hay búsqueda

    //console.log( "cambio de página:", page)

    loadContacts();
  }, [page]);

   useEffect(() => {
     setPage(1);
   }, [search, empresaFilter, cargoFilter]);

  const getPaginationItems = () => {
    const pages: (number | 'ellipsis')[] = [];

    if (totalPages <= 10) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 4) pages.push('ellipsis');

      const start = Math.max(2, page - 2);
      const end = Math.min(totalPages - 1, page + 2);

      for (let i = start; i <= end; i++) pages.push(i);

      if (page < totalPages - 3) pages.push('ellipsis');
      pages.push(totalPages);
    }

    return pages;
  };

  const handleSave = async () => {
    if (!selectedContact) return;

    const validationErrors = validateContact(selectedContact);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await updateContact(selectedContact.id, selectedContact, token);
      setShowModal(false);
      loadContacts();
      setErrors({});
    } catch (err) {
      console.error('Error al guardar contacto:', err);
    }
  };

  const validateContact = (contact: Contact) => {
    const newErrors: { [key: string]: string } = {};

    if (!contact.apenomafi.trim()) newErrors.apenomafi = 'El nombre es obligatorio';
    if (!/^\d{7,15}$/.test(contact.celular)) newErrors.celular = 'Teléfono inválido';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) newErrors.email = 'Correo inválido';

    return newErrors;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setPage(1);
      loadContacts();
    }
  };

  return (    
    <Container className="mt-4">
      <h2>Contactos</h2>
      <Form className="mb-3" onSubmit={(e) => { e.preventDefault(); setPage(1); loadContacts(); }}> 
        <Form.Control
          type="text"
          placeholder="Buscar Nombre o cédula"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          className="mb-2"
        />
        <Form.Control
          type="text"
          placeholder="Filtrar por empresa o ruc"
          value={empresaFilter}
          onChange={(e) => setEmpresaFilter(e.target.value)}
          onKeyDown={handleKeyDown}
          className="mb-2"
        />
        <Form.Control
          type="text"
          placeholder="Filtrar cargo u ocupación"
          value={cargoFilter}
          onChange={(e) => setCargoFilter(e.target.value)}
          onKeyDown={handleKeyDown}
          className="mb-2"
        />
      </Form>      
      <Button variant="success" className='d-none' onClick={() => exportToExcel(contacts)}>
        Exportar a Excel
      </Button>      
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Cédula</th>
            <th>Teléfono</th>
            <th>Correo</th>
            <th>Empresa</th>
            <th>Ocupación</th>
            <th>Sueldo</th>            
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={6} className="text-center">
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Cargando contactos...
              </td>
            </tr>
          ) : contacts.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center">No se encontraron contactos</td>
            </tr>
          ) : (
            contacts.map((c) => (
              <tr key={c.id}>
                <td>{c.apenomafi}</td>
                <td>{c.numafi}</td>
                <td>{c.celular}</td>
                <td>{c.email}</td>
                <td>{c.nomempresa}</td>                
                <td>{c.ocuafi}</td>
                <td>{c.sueldo}</td>
                <td>
                  <Button variant="info" size="sm" className="me-2" onClick={() => handleDetails(c.id)}>Detalles</Button>
                  <Button variant="danger" className='d-none' size="sm" onClick={() => handleDelete(c.id)} disabled>Eliminar</Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <Pagination>
        <Pagination.First onClick={() => setPage(1)} disabled={page === 1 || loading} />
        <Pagination.Prev onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1 || loading} />

        {getPaginationItems().map((item, index) =>
          item === 'ellipsis' ? (
            <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
          ) : (
            <Pagination.Item
              key={item}
              active={item === page}
              onClick={() => setPage(item)}
              disabled={loading}
            >
              {item}
            </Pagination.Item>
          )
        )}

        <Pagination.Next onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages || loading} />
        <Pagination.Last onClick={() => setPage(totalPages)} disabled={page === totalPages || loading} />
      </Pagination>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg"> 
        <Modal.Header closeButton>
          <Modal.Title className="h5">Detalles del contacto</Modal.Title>
        </Modal.Header>
        
        {/* Cuerpo de la modal compacto y con campos en línea */}
        <Modal.Body className="small-modal-body"> 
          {selectedContact ? (
            <Form>
              
              {/* 1. Nombre (Apenomafi) - Editable y requerido */}
              <RenderInlineField
                label="Nombre"
                field="apenomafi"
                contact={selectedContact}
                setContact={setSelectedContact}
                error={errors.apenomafi}
                readOnly
              />

              <RenderInlineField
                label="Cédula"
                field="numafi"
                contact={selectedContact}
                setContact={setSelectedContact}
                error={errors.celular}
                readOnly
              />

              <RenderInlineField
                label="Teléfono"
                field="celular"
                contact={selectedContact}
                setContact={setSelectedContact}
                error={errors.celular}
                readOnly
              />
              
              <RenderInlineField
                label="Correo"
                field="email"
                type="email"
                contact={selectedContact}
                setContact={setSelectedContact}
                error={errors.email}
                readOnly
              />
              
              <RenderInlineField
                label="Dirección"
                field="dirafi"
                contact={selectedContact}
                setContact={setSelectedContact}
                error={errors.email}
                readOnly
              />
              
              <RenderInlineField
                label="Ocupación"
                field="ocuafi"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />
                            
              <RenderInlineField
                label="Sueldo"
                field="sueldo"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

              <RenderInlineField
                label="Empresa"
                field="nomempresa"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

              <RenderInlineField
                label="Ruc Empresa"
                field="rucemp"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

              <RenderInlineField
                label="Dirección Empresa"
                field="dirsuc"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

              <RenderInlineField
                label="Tipo Empresa"
                field="tipoempresa"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

              <RenderInlineField
                label="Teléfono Empresa"
                field="telsuc"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

            </Form>
          ) : (
            <p>Cargando...</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button className="d-none" variant="primary" onClick={handleSave}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}

export default ContactsPage;